import React from 'react';
import { Card } from 'react-bootstrap';
import { formatDateTime } from '../utils/dates';

export const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
export const moneyShort = (n) => new Intl.NumberFormat('es-DO', { notation: 'compact', maximumFractionDigits: 1 }).format(n || 0);
export const pct = (n) => (n === null || n === undefined ? '—' : `${n.toFixed(1)}%`);
// The server sees IPv4 clients as "::ffff:1.2.3.4"; show the address people recognise.
export const cleanIp = (ip) => (ip ? String(ip).replace(/^::ffff:/i, '') : '');
export const dateTime = (d) => formatDateTime(d);

export const COLORS = { cyan: '#12B8B2', green: '#4ADE80', amber: '#F5B700', red: '#F87171', blue: '#60A5FA', purple: '#A78BFA', gray: '#9DB3C2' };
export const PALETTE = [COLORS.cyan, COLORS.blue, COLORS.amber, COLORS.purple, COLORS.green, COLORS.red, COLORS.gray];

export const chartMoneyOptions = (extra = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.dataset.label ? `${c.dataset.label}: ` : ''}${money(c.parsed.y ?? c.parsed.x ?? c.parsed)}` } } },
  scales: { y: { ticks: { callback: (v) => moneyShort(v) } } },
  ...extra,
});

// A headline number with its change against the previous period. `inverse`
// flips the colour logic for measures where going up is bad (expenses, credit
// notes, receivables).
export function Kpi({ label, value, current, previous, inverse = false, sub, tone, hint }) {
  let delta = null;
  if (typeof current === 'number' && typeof previous === 'number' && previous !== 0) {
    delta = ((current - previous) / Math.abs(previous)) * 100;
  }
  const good = delta === null ? null : (inverse ? delta < 0 : delta > 0);
  return (
    <Card className="shadow-sm p-3 dashboard-card h-100" title={hint}>
      <div className="text-muted small">{label}</div>
      <div className="mt-1" style={{ fontSize: 'clamp(1.05rem, 2.1vw, 1.55rem)', fontWeight: 600, lineHeight: 1.15, whiteSpace: 'nowrap', color: tone ? COLORS[tone] : undefined }}>{value}</div>
      {delta !== null && Math.abs(delta) >= 0.05 && (
        <div className="small mt-1" style={{ color: good ? COLORS.green : COLORS.red }}>
          {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}% <span className="text-muted">vs. período anterior</span>
        </div>
      )}
      {delta === null && typeof previous === 'number' && previous === 0 && typeof current === 'number' && current !== 0 && (
        <div className="small mt-1 text-muted">sin dato previo</div>
      )}
      {sub && <div className="small text-muted mt-1">{sub}</div>}
    </Card>
  );
}

export function ChartCard({ title, subtitle, height = 260, empty, children }) {
  return (
    <Card className="shadow-sm p-3 h-100">
      <Card.Title className="mb-0" style={{ fontSize: '1.05rem' }}>{title}</Card.Title>
      {subtitle && <div className="small text-muted mb-2">{subtitle}</div>}
      <div style={{ height, marginTop: subtitle ? 0 : 8 }} className={empty ? 'd-flex align-items-center justify-content-center text-muted' : ''}>
        {empty ? 'Sin datos en este período' : children}
      </div>
    </Card>
  );
}

export const STATUS_LABEL = {
  draft: 'Borrador', ready: 'Lista', pending_dgii: 'Verificando DGII', issued: 'Emitida', rejected: 'Rechazada DGII',
  paid: 'Pagada', partially_paid: 'Pago parcial', void: 'Anulada',
};

export const TIPO_LABEL = {
  E31: 'E31 Crédito Fiscal', E32: 'E32 Consumo', E33: 'E33 Nota de débito', E34: 'E34 Nota de crédito',
  E44: 'E44 Reg. Especial', E45: 'E45 Gubernamental',
};

export const METHOD_LABEL = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta', cheque: 'Cheque' };

export function monthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return `${['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][m - 1]} ${String(y).slice(2)}`;
}

const FIELD_LABELS = {
  number: 'Número', ncf: 'NCF', tipoEcf: 'Tipo de comprobante', client: 'Cliente', status: 'Estado', dueDate: 'Vencimiento',
  subtotal: 'Subtotal', itbisTotal: 'ITBIS', total: 'Total', amountPaid: 'Pagado', creditedAmount: 'Acreditado',
  name: 'Nombre', rnc: 'RNC', cedula: 'Cédula', email: 'Correo', phone: 'Teléfono', address: 'Dirección', tipo: 'Tipo',
  price: 'Precio', cost: 'Costo', sku: 'Código', category: 'Categoría', description: 'Descripción', amount: 'Monto',
  method: 'Método', date: 'Fecha', vendor: 'Proveedor', notes: 'Notas', role: 'Rol', username: 'Usuario', active: 'Activo',
  invoice: 'Factura', product: 'Producto', unidadMedida: 'Unidad de medida', reason: 'Motivo', createdBy: 'Creado por',
  modificationReason: 'Motivo de la nota', modifiedNcf: 'Modifica el NCF', 'ecf.status': 'Estado e-CF', 'ecf.dgiiStatus': 'Veredicto DGII',
  'ecf.dgiiMessage': 'Mensaje DGII',
};

// "items[1]" -> "Línea 2"; known fields get Spanish names, anything else stays as stored.
export function fieldLabel(field) {
  const line = field.match(/^items\[(\d+)\]$/);
  if (line) return `Línea ${Number(line[1]) + 1}`;
  return FIELD_LABELS[field] || field;
}

export function displayValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  return String(v);
}
